import { useCallback, useEffect, useState } from "react";
import {
  IdCard, PlayCircle, AlertTriangle, CheckCircle2, Loader2, KeyRound, Copy,
  Eye, EyeOff, Activity, ArrowLeft,
} from "lucide-react";
import { PageHeader, Card, SectionTitle } from "@/components/common/PageHeader";
import { Loading, Stat } from "@/pages/_shared";
import { toast } from "sonner";
import api from "@/lib/api";

function StatusBadge({ status }) {
  const map = {
    "Client Action Needed": "bg-amber-100 text-amber-700",
    "Working": "bg-blue-50 text-[#1a5fd0]", "Submitted": "bg-blue-50 text-[#1a5fd0]",
    "Not Started": "bg-slate-100 text-slate-500", "Purchase Needed": "bg-violet-50 text-violet-700",
    "Declined": "bg-red-50 text-red-600",
  };
  const cls = map[status] || "bg-emerald-50 text-emerald-600";
  return <span className={`rounded-full px-2 py-0.5 font-mono text-[9px] font-bold uppercase tracking-wider ${cls}`}>{status}</span>;
}

function Cred({ value }) {
  const [show, setShow] = useState(false);
  if (!value) return <span className="text-slate-300">—</span>;
  return (
    <span className="inline-flex items-center gap-1.5">
      <code className="rounded bg-slate-100 px-1.5 py-0.5 font-mono text-[11px] text-slate-700">{show ? value : "•".repeat(10)}</code>
      <button onClick={() => setShow((v) => !v)} className="text-slate-400 hover:text-slate-700">{show ? <EyeOff size={13} /> : <Eye size={13} />}</button>
      <button onClick={() => { navigator.clipboard.writeText(value); toast.success("Copied"); }} className="text-slate-400 hover:text-slate-700"><Copy size={12} /></button>
    </span>
  );
}

function ClientDetail({ userId, onBack, onChanged }) {
  const [detail, setDetail] = useState(null);
  const load = useCallback(() => { api.get(`/business-credit/admin/client/${userId}`).then(({ data }) => setDetail(data)); }, [userId]);
  const [runningApp, setRunningApp] = useState(null);
  const submitApp = async (id) => {
    setRunningApp(id);
    try {
      const { data } = await api.post("/business-credit/admin/submit-application", { account_id: id });
      const r = data.result;
      if (r.status === "Submitted") toast.success(`Submitted via browser automation — confirmation ${r.confirmation}`);
      else toast(`Auto-filled & stopped for the owner: ${r.reason}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "The Application Runner could not submit this one.");
    } finally { setRunningApp(null); }
  };
  const viewArtifact = async (id) => {
    try {
      const res = await api.get(`/business-credit/artifact/${id}`, { responseType: "blob" });
      window.open(URL.createObjectURL(res.data), "_blank");
    } catch { toast.error("Could not load the submission proof."); }
  };
  useEffect(() => { load(); }, [load]);
  if (!detail) return <Loading />;

  return (
    <div className="space-y-6 animate-fade-up" data-testid="bc-client-detail">
      <button onClick={onBack} data-testid="bc-back" className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-slate-500 hover:text-slate-900"><ArrowLeft size={15} /> All clients</button>
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="font-head text-2xl font-extrabold text-slate-900">{detail.client.company || detail.client.name}</h2>
          <div className="text-[13px] text-slate-500">{detail.client.name} · {detail.client.email}</div>
        </div>
        {!detail.profile_complete && (
          <span className="rounded-full bg-amber-50 px-3 py-1 font-mono text-[10px] font-bold uppercase text-amber-600">Profile missing: {detail.profile_missing.slice(0, 3).join(", ")}</span>
        )}
      </div>

      {detail.run?.summary && (
        <Card className="border-[#080d1a]/10 bg-slate-50 p-4" data-testid="bc-run-summary">
          <SectionTitle icon={Activity}>AI Completion Report</SectionTitle>
          <p className="whitespace-pre-line text-[13px] leading-relaxed text-slate-700">{detail.run.summary}</p>
        </Card>
      )}

      <Card className="p-5">
        <SectionTitle icon={IdCard}>Vendor Accounts</SectionTitle>
        <div className="overflow-x-auto">
          <table className="w-full text-left text-[13px]" data-testid="bc-accounts-table">
            <thead>
              <tr className="border-b border-slate-100 font-mono text-[9px] uppercase tracking-wider text-slate-400">
                <th className="py-2 pr-3">Vendor</th><th className="py-2 pr-3">Status</th><th className="py-2 pr-3">Username</th><th className="py-2 pr-3">Password</th><th className="py-2 pr-3">Payment</th><th className="py-2 pr-3">Reporting</th><th className="py-2 pr-3">Next / Action</th><th className="py-2">Application Runner</th>
              </tr>
            </thead>
            <tbody>
              {detail.accounts.map((a) => (
                <tr key={a.id} className="border-b border-slate-50 align-top" data-testid="bc-account-row">
                  <td className="py-2.5 pr-3"><div className="font-semibold text-slate-800">{a.vendor_name}</div><div className="text-[11px] text-slate-400">{a.terms}</div></td>
                  <td className="py-2.5 pr-3"><StatusBadge status={a.status} /></td>
                  <td className="py-2.5 pr-3 text-slate-600">{a.username || "—"}</td>
                  <td className="py-2.5 pr-3"><Cred value={a.password} /></td>
                  <td className="py-2.5 pr-3 font-mono text-[11px] text-slate-500">{a.payment_due || "—"}</td>
                  <td className="py-2.5 pr-3 text-[11px] text-slate-500">{a.reporting_status || "—"}</td>
                  <td className="py-2.5 pr-3 text-[12px] text-slate-600">
                    {a.action_required ? <span className="text-amber-700">{a.action_required.what}</span> : (a.purchase?.required ? <span className="text-violet-700">{a.purchase.note}</span> : <span className="text-slate-400">—</span>)}
                  </td>
                  <td className="py-2.5">
                    <button onClick={() => submitApp(a.id)} disabled={runningApp === a.id} data-testid="run-application-btn"
                      className="inline-flex items-center gap-1 rounded-lg bg-[#080d1a] px-2.5 py-1.5 text-[11px] font-bold text-white hover:opacity-90 disabled:opacity-50">
                      {runningApp === a.id ? <Loader2 size={12} className="animate-spin" /> : <PlayCircle size={12} />} Auto-Fill &amp; Submit
                    </button>
                    {a.submission?.confirmation && <div className="mt-1 text-[10px] font-bold text-emerald-600">✓ {a.submission.confirmation}</div>}
                    {a.submission?.artifact_id && <button onClick={() => viewArtifact(a.submission.artifact_id)} className="mt-0.5 block text-[10px] text-[#1a5fd0] underline">view proof</button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {detail.activity.length > 0 && (
        <Card className="p-5">
          <SectionTitle icon={Activity}>AI Activity</SectionTitle>
          <ul className="space-y-2">
            {detail.activity.map((x) => (
              <li key={`${x.when}-${x.action.slice(0, 20)}`} className="flex items-start gap-2 text-[13px] text-slate-600">
                <span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-[#1a5fd0]" />
                <span><span className="font-semibold text-slate-800">{x.actor}</span> {x.action}</span>
              </li>
            ))}
          </ul>
        </Card>
      )}
    </div>
  );
}

export default function BusinessCreditAdmin() {
  const [data, setData] = useState(null);
  const [selected, setSelected] = useState(null);
  const [runningId, setRunningId] = useState(null);

  const load = useCallback(() => { api.get("/business-credit/admin/overview").then(({ data }) => setData(data)).catch(() => setData(null)); }, []);
  useEffect(() => { load(); }, [load]);

  const runSetup = async (uid) => {
    setRunningId(uid);
    try {
      const { data: res } = await api.post("/business-credit/admin/run", { user_id: uid });
      toast.success(`Setup complete — ${res.run.counts.done} active, ${res.run.counts.action} need client action`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not run setup");
    } finally {
      setRunningId(null);
    }
  };

  if (!data) return <Loading />;
  if (selected) return <div className="mx-auto max-w-6xl"><ClientDetail userId={selected} onBack={() => { setSelected(null); load(); }} onChanged={load} /></div>;

  const t = data.totals;
  return (
    <div className="space-y-6 animate-fade-up" data-testid="bc-admin-page">
      <PageHeader eyebrow="Business Credit AI Employee" title="Credit Operations"
        subtitle="Set up and track vendor / business-credit accounts for every client. DONE · WORKING · NEEDS ATTENTION." />

      <div className="grid gap-4 sm:grid-cols-4">
        <Stat label="Done" value={t.done} tone="green" />
        <Stat label="Working" value={t.working} tone="blue" />
        <Stat label="Needs Attention" value={t.action} tone="red" />
        <Stat label="Clients set up" value={`${t.clients_with_setup}/${t.clients}`} />
      </div>

      <Card className="p-5">
        <SectionTitle icon={IdCard}>Clients</SectionTitle>
        <div className="overflow-x-auto">
          <table className="w-full text-left text-[13px]" data-testid="bc-clients-table">
            <thead>
              <tr className="border-b border-slate-100 font-mono text-[10px] uppercase tracking-wider text-slate-400">
                <th className="py-2 pr-4">Client</th><th className="py-2 pr-4">Accounts</th><th className="py-2 pr-4">Done</th><th className="py-2 pr-4">Working</th><th className="py-2 pr-4">Needs Action</th><th className="py-2"></th>
              </tr>
            </thead>
            <tbody>
              {data.clients.map((c) => (
                <tr key={c.user_id} className="border-b border-slate-50" data-testid="bc-client-row">
                  <td className="py-2.5 pr-4"><button onClick={() => setSelected(c.user_id)} className="text-left font-semibold text-slate-800 hover:text-[#1a5fd0]" data-testid="bc-client-open">{c.company || "—"}</button><div className="text-[11px] text-slate-400">{c.email}</div></td>
                  <td className="py-2.5 pr-4 text-slate-600">{c.accounts || <span className="text-slate-300">—</span>}</td>
                  <td className="py-2.5 pr-4"><span className="text-emerald-600">{c.done}</span></td>
                  <td className="py-2.5 pr-4"><span className="text-[#1a5fd0]">{c.working}</span></td>
                  <td className="py-2.5 pr-4">{c.action > 0 ? <span className="inline-flex items-center gap-1 text-amber-600"><AlertTriangle size={13} />{c.action}</span> : <CheckCircle2 size={14} className="text-slate-300" />}</td>
                  <td className="py-2.5 text-right">
                    <button onClick={() => runSetup(c.user_id)} disabled={runningId === c.user_id} data-testid="bc-run-btn"
                      className="inline-flex items-center gap-1.5 rounded-lg bg-[#5B4BE6] px-3 py-1.5 font-head text-[12px] font-bold text-white hover:bg-[#4F46E5] disabled:opacity-60">
                      {runningId === c.user_id ? <Loader2 size={14} className="animate-spin" /> : <PlayCircle size={14} />}
                      {c.has_run ? "Re-run" : "Run Setup"}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {data.activity.length > 0 && (
        <Card className="p-5">
          <SectionTitle icon={Activity}>Recent AI Activity</SectionTitle>
          <ul className="space-y-2">
            {data.activity.map((x) => (
              <li key={`${x.when}-${x.action.slice(0, 20)}`} className="flex items-start gap-2 text-[13px] text-slate-600">
                <span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-[#1a5fd0]" />
                <span><span className="font-semibold text-slate-800">{x.actor}</span> {x.action}</span>
              </li>
            ))}
          </ul>
        </Card>
      )}
    </div>
  );
}
